import React, { useRef } from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { Images } from "@/assets/images";
import { OmnipodSource, useOmnipodSource } from "@/hooks/useOmnipodSource";
import { useStyles } from "@/hooks/useStyles";
import { OmnipodConstants } from "@/lib/drivers/insulet/OmnipodConstants";
import { OmnipodDriver } from "@/lib/drivers/insulet/OmnipodDriver";
import { MedicalDevice } from "@/models/DeviceModel";
import { Queries } from "@/queries/Queries";
import { Theme } from "@/theme/CreateTheme.ts";
import { BaseDeviceCardProps, DeviceCard } from "@/uiKit/atoms/device-card/DeviceCard";
import { DeviceSourcesStatus } from "@/uiKit/atoms/device-card/DeviceSourcesStatus";
import { DeviceUploadAction } from "@/uiKit/atoms/device-card/DeviceUploadAction";
import { LastDeviceUpload } from "@/uiKit/atoms/device-card/LastDeviceUpload";
import { OmnipodSearchButton } from "@/uiKit/molecules/OmnipodSearchButton.tsx";
import { ProgressListener } from "@/utils/ProgressMixin";
import { Sentry } from "@/utils/sentryUtils";

export const OMNIPOD_DEVICE_CARD_ID = "omnipod";

const omnipodDriver = async (
  devices: MedicalDevice[],
  progressId: string,
  progressListener: ProgressListener,
  source: OmnipodSource,
) => {
  try {
    const driver = new OmnipodDriver(source, devices);
    driver.subscribeToProgress(progressId, progressListener);
    return driver;
  } catch (e) {
    Sentry.captureException(e);
    console.error("Error", e);
  }
  return undefined;
};

export const OmnipodDeviceCard: React.FC<BaseDeviceCardProps> = ({
  onRawData,
  onData,
  onError,
  progress,
  progressListener,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const erosDevices = Queries.device.useDevicesByType(OmnipodConstants.EROS_API_DEVICE_NAME);
  const dashDevices = Queries.device.useDevicesByType(OmnipodConstants.DASH_API_DEVICE_NAME);
  const devices = [...erosDevices, ...dashDevices];

  const sources = useOmnipodSource();

  const driverRef = useRef<OmnipodDriver | undefined>(undefined);

  const onUpload = async (sources: OmnipodSource[]) => {
    const driver = await omnipodDriver(devices, OMNIPOD_DEVICE_CARD_ID, progressListener, sources[0]);
    driverRef.current = driver;
    console.info("Driver initialized", driver ? "successfully" : "unsuccessfully");
    if (driver) {
      const onErrorWrapped = (message: string) => onError(t(message));
      await driver.fetchAndUpload(onData, onErrorWrapped, onRawData);
    } else {
      onError(t("devices.errors.driverInit"));
    }
  };

  const onCancel = () => {
    driverRef.current?.disconnect();
    onError(t("devices.errors.cancelled"));
  };

  return (
    <DeviceCard
      deviceName={t("devices.omnipod.name")}
      deviceInstructions={
        sources.length > 0 ? t("devices.omnipod.instructions") : t("devices.omnipod.instructionsNoDevices")
      }
      action={
        <div className={styles.action}>
          {sources.length > 0 ? (
            <DeviceUploadAction
              onUpload={() => onUpload(sources)}
              onCancel={onCancel}
              progress={progress}
              canUpload={sources.length === 1 && !progress}
            />
          ) : (
            <OmnipodSearchButton />
          )}
        </div>
      }
      classes={{ content: styles.content }}
    >
      <div className={styles.info}>
        <Images.omnipodPump alt={t("devices.omnipod.name")} width={100} />
        <div>
          <LastDeviceUpload devices={devices} />
          <DeviceSourcesStatus
            sources={sources}
            noDeviceText={t("devices.omnipod.noDeviceFound")}
            deviceDetectedText={t("devices.omnipod.deviceDetected")}
            manyDevicesDetectedText={t("devices.omnipod.manyDevicesDetected")}
          />
        </div>
      </div>
      <OmnipodFileUpload onUpload={onUpload} />
    </DeviceCard>
  );
};

type OmnipodFileUploadProps = {
  onUpload: (sources: OmnipodSource[]) => void;
};

const OmnipodFileUpload: React.FC<OmnipodFileUploadProps> = ({ onUpload }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const styles = useStyles(makeStyles);

  return (
    <div>
      <div onClick={() => fileInputRef.current?.click()} className={styles.uploadManuallyButton}>
        {t("devices.omnipod.uploadManually")}
      </div>
      <input
        className={styles.uploadManuallyInput}
        type="file"
        ref={fileInputRef}
        accept=".ibf"
        onChange={e => {
          if (e.currentTarget.files) {
            const file = e.currentTarget.files[0];
            onUpload([{ type: "manual", file }]);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
        }}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(16),
  }),
  info: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(16),
  }),
  action: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(4),
  }),
  uploadManuallyButton: css({
    cursor: "pointer",
    color: theme.palette.primary.main,
    padding: theme.spacing(2, 8),
    "&:hover": {
      backgroundColor: theme.palette.grey[50],
    },
  }),
  uploadManuallyInput: css({
    display: "none",
  }),
});
